import styled from 'styled-components';
import { Header } from '../../components/header';
import Footer from '../../components/footer';

const TermsContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;
  line-height: 1.6;
  color: #333;
  counter-reset: section;
  margin-top: 80px;
`;

const TermsHeader = styled.header`
  margin-bottom: 30px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 22px;
  margin-bottom: 10px;
  font-weight: bold;
`;

const VersionInfo = styled.div`
  font-size: 20px;
  margin-bottom: 10px;
  span {
    margin-right: 5px;
    font-weight: bold;
  }
  span:not(:first-child) {
    margin-left: 15px;
  }

  @media (max-width: 480px) {
    display: flex;
    flex-direction: column;
    
    span:not(:first-child) {
      margin-left: 0;
      margin-top: 5px;
    }
  }
`;

const TermsContent = styled.div`
  font-size: 20px;
`;

const Section = styled.section`
  margin-bottom: 10px;
  counter-increment: section;
  counter-reset: subsection;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  margin-bottom: 5px;

  &::before {
    content: counter(section) ". ";
  }
`;

const SubsectionList = styled.ol`
  list-style-type: none;
  padding-left: 0;
  margin: 0;
`;

const Subsection = styled.li`
  margin-bottom: 15px;
  counter-increment: subsection;
  padding-left: 40px;
  position: relative;
`;

const SubsectionTitle = styled.span`
  position: absolute;
  left: 0;
  &::before {
    content: counter(section) "." counter(subsection) ". ";
  }
`;

const SubsectionContent = styled.span`
  margin-left: 0;
`;

const EmailTemplate = styled.div`
  margin-left: 20px;
  line-height: 1.8;
`;

const PrivacyPolicy = () => {
  return (
    <>
      <Header />
      <TermsContainer>
        <TermsHeader>
          <Title>Privacy Policy for Vyga</Title>
          <VersionInfo>
            <span>Version:</span> v.1.4
          </VersionInfo>
          <VersionInfo>
            <span>Updated:</span> 2025 March 25
          </VersionInfo>
        </TermsHeader>

        <TermsContent>
          <p>
            By accessing or using Vyga (the "Platform”), you are agreeing to Vyga’s privacy and security 
            policy (“Privacy Policy”) which is incorporated by reference under Vyga’s Terms and Conditions
            (the “Agreement”). Please read the Agreement and this Privacy Policy carefully before using the 
            Platform. If you do not agree to both the Privacy Policy’s and Agreement’s terms, you must not 
            use the Platform.
          </p>

          <Section>
            <SectionTitle>&nbsp;&nbsp;Privacy and Security</SectionTitle>
            <SubsectionList>
              <Subsection>
                <SubsectionTitle />
                    <SubsectionContent><u>Personal Data Collection:</u> We are committed to protecting your privacy and ensuring that 
                    your personal information is handled securely. We collect and use your personal data only
                    as necessary for the operation of the Platform, in accordance with our Privacy Policy. The
                    Platform does not currently require that you provide any personalized information that 
                    could connect your personal identity to the email address you provide to the Platform or 
                    Platform username (or password) associated with your Platform account.  The Platform 
                    recommends that you only supply an email or select a username/password that cannot be 
                    traced to your personal identity, such that if you do not follow this recommendation, then 
                    you accept full responsibility for any ramifications of data breaches.
                    </SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>Personal Data Use:</u> We will use the email and other contact data provided by you to the 
                    Platform to enable the Platform’s different monetization strategies including for example 
                    but not limited to targeted communication of third-party educational or other content 
                    delivered by the Platform to you and other users.
                </SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                    <SubsectionContent><u>Data Protection:</u> We will implement reasonable and industry-standard security measures, 
                        including encryption, access controls, and regular security assessments, to protect your 
                        data from unauthorized access, disclosure, alteration, or destruction.
                    </SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                    <SubsectionContent><u>Data Sharing: </u> We will not share your personal data with third parties except as 
                        necessary to provide the services offered on the Platform, comply with legal obligations, 
                        or protect our rights.  However, by using this Platform you hereby agree that Platform can
                        deidentify your personal data along with associated usage data and then aggregate that 
                        deidentified data with other users’ deidentified data to monetize said data in ways listed 
                        above.
                    </SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                    <SubsectionContent><u>Compliance with Regulations: </u> We comply with relevant data protection laws, including 
                    the General Data Protection Regulation (GDPR), and provide users with the ability to 
                    control their data (by emailing us per the Contact Information section below).
                    </SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                    <SubsectionContent><u>Right to Delete Personal Data: </u> Upon request, and subject to applicable laws, you can 
                        request the deletion of your personal data. We will comply with your request as required 
                        by law, but certain data may be retained for regulatory, security, or legal purposes.
                    </SubsectionContent>
              </Subsection>
            </SubsectionList>
          </Section>

          <Section>
            <SectionTitle>&nbsp;&nbsp;User Consent Management</SectionTitle>
            <SubsectionList>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>Consent Management:</u> You have the right to manage your data sharing preferences 
                    through the Platform. You can choose what data to share with the Platform and revoke 
                    consent at any time per this section.</SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>Consent to Data Collection:</u> By using the Platform, you consent to the collection, storage,
                    and use of your personal and usage data as outlined in sections 2 and 3 above and 
                    elsewhere in these Terms & Conditions.</SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent><u>Blockchain-based Consent:</u> In certain cases, we may utilize blockchain technology to 
                    record and maintain a transparent, immutable record of your consent, ensuring that you 
                    have full control over your data.</SubsectionContent>
              </Subsection>
            </SubsectionList>
          </Section>

          <Section>
            <SectionTitle>&nbsp;&nbsp;Governing Law.</SectionTitle>
            <SubsectionList>
            <Subsection>
            <SubsectionTitle />
              <SubsectionContent>This Privacy Policy’s governing law will be dictated by each user’s applicable region, 
              e.g. US law for US citizens or residents, EU law for EU citizens or residents.</SubsectionContent>
            </Subsection>
            <Subsection>
            <SubsectionTitle />
              <SubsectionContent>Otherwise, this Privacy Policy and the Agreement under which it operates, as well as any 
                disputes or claims arising out of or in connection with your use of the Platform, shall be 
                governed by and construed in accordance with the laws of Clark County, Washington 
                state, USA, without regard to its conflict of law principles. By accessing or using the 
                Platform, you agree that any legal action or proceeding related to these Terms and 
                Conditions shall be exclusively brought in the courts located in Vancouver, Clark County,
                Washington state, USA, and you consent to the personal jurisdiction of such courts. If any
                provision of these Terms and Conditions is found to be unlawful, void, or unenforceable, 
                such provision will be deemed severable from these Terms and Conditions and will not 
                affect the validity and enforceability of the remaining provisions.</SubsectionContent>
            </Subsection>
            </SubsectionList>
            
          </Section>

          <Section>
            <SectionTitle>&nbsp;&nbsp;Contact Information</SectionTitle>
            <SubsectionList>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent>
                  <u>Platform Contact Information:</u> For any questions or concerns regarding these Terms, or desire to notify the Platform of your intent to exercise certain GDPR or similar data rights, please contact us at:<br />
                  Vyga Inc.<br />
                  <a href="mailto:contact@vyga.co"><u>Admin@vyga.com</u></a>
                </SubsectionContent>
              </Subsection>
              <Subsection>
                <SubsectionTitle />
                <SubsectionContent>
                  <u>Your Communication Format:</u> You agree that the subject line of your email will follow 1 of the following 3 templates:
                  <EmailTemplate>
                    a) "Questions regarding Terms & Conditions"<br />
                    b) "Concerns regarding Terms & Conditions"<br />
                    c) "Desire to Exercise GDPR or Similar Data Rights"
                  </EmailTemplate>
                </SubsectionContent>
              </Subsection>
            </SubsectionList>
          </Section>
        </TermsContent>
      </TermsContainer>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;